/**
 * Flow-Position ID utilities
 *
 * Generates human-readable IDs for nodes based on their flow and position.
 * Format: {flow}-{position} e.g., "1-1", "1-2", "2-1"
 *
 * - Flow: Which start node the node belongs to (1, 2, 3...)
 * - Position: Order within that flow (1, 2, 3...)
 */

import { DiagramEdge, DiagramNode } from "components/CC/Diagrams/ConnectionCreatorDiagram/ConnectionCreatorDiagram";

/**
 * Calculate flow-position IDs for all nodes in a diagram.
 * Uses BFS from each start node to determine flow membership and position.
 */
export const calculateFlowPositionIds = (nodes: DiagramNode[], edges: DiagramEdge[]): Map<string, string> => {
    const idMap = new Map<string, string>(); // old ID -> flow-position ID

    // Find all start nodes
    const startNodes = nodes.filter((n) => n.type === "startNode");

    // Build adjacency list for traversal
    const adjacency = new Map<string, string[]>();
    edges.forEach((edge) => {
        const sources = adjacency.get(edge.source) || [];
        sources.push(edge.target);
        adjacency.set(edge.source, sources);
    });

    // Track visited nodes to handle shared nodes between flows
    const visited = new Set<string>();

    // Process each flow (starting from each start node)
    startNodes.forEach((startNode, flowIndex) => {
        const flowNumber = flowIndex + 1;
        let position = 0;

        // BFS traversal
        const queue: string[] = [startNode.id];

        while (queue.length > 0) {
            const nodeId = queue.shift();
            if (!nodeId) continue;

            if (visited.has(nodeId)) continue;
            visited.add(nodeId);

            position++;
            const flowPositionId = `${flowNumber}-${position}`;
            idMap.set(nodeId, flowPositionId);

            // Add connected nodes to queue
            const connectedNodes = adjacency.get(nodeId) || [];
            connectedNodes.forEach((targetId) => {
                if (!visited.has(targetId)) {
                    queue.push(targetId);
                }
            });
        }
    });

    // Handle orphan nodes (not connected to any start)
    let orphanPosition = 0;
    nodes.forEach((node) => {
        if (!visited.has(node.id)) {
            orphanPosition++;
            idMap.set(node.id, `0-${orphanPosition}`); // Flow 0 for orphans
        }
    });

    return idMap;
};

/**
 * Transform nodes to use flow-position IDs.
 * Stores original ID in data.beId if it looks like a UUID.
 */
export const transformNodesToFlowPositionIds = (
    nodes: DiagramNode[],
    edges: DiagramEdge[]
): { nodes: DiagramNode[]; edges: DiagramEdge[]; idMap: Map<string, string> } => {
    const idMap = calculateFlowPositionIds(nodes, edges);

    const transformedNodes = nodes.map((node) => {
        const newId = idMap.get(node.id) || node.id;
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(node.id);

        return {
            ...node,
            id: newId,
            data: {
                ...node.data,
                // Store original UUID for BE sync (only if it's a UUID)
                ...(isUuid && { beId: node.id }),
            },
        };
    });

    const transformedEdges = edges.map((edge) => ({
        ...edge,
        id: `edge-${idMap.get(edge.source) || edge.source}-${idMap.get(edge.target) || edge.target}`,
        source: idMap.get(edge.source) || edge.source,
        target: idMap.get(edge.target) || edge.target,
    }));

    return { nodes: transformedNodes, edges: transformedEdges, idMap };
};

/**
 * Transform nodes back to UUIDs for BE save.
 * Uses beId if available, generates new UUID if not.
 */
export const transformNodesToBeIds = (
    nodes: DiagramNode[],
    edges: DiagramEdge[]
): { nodes: DiagramNode[]; edges: DiagramEdge[] } => {
    // Build reverse map: flow-position ID -> beId (or generate new)
    const idMap = new Map<string, string>();

    nodes.forEach((node) => {
        const beId = (node.data as Record<string, unknown>)?.beId as string | undefined;
        // Use existing beId or generate new UUID
        const targetId = beId || crypto.randomUUID();
        idMap.set(node.id, targetId);
    });

    const transformedNodes = nodes.map((node) => {
        const newId = idMap.get(node.id) ?? node.id;
        // Remove beId from data since it's now the actual ID
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { beId: _beId, ...restData } = (node.data ?? {}) as Record<string, unknown>;

        return {
            ...node,
            id: newId,
            data: restData,
        };
    });

    const transformedEdges = edges.map((edge) => ({
        ...edge,
        id: `edge-${idMap.get(edge.source) || edge.source}-${idMap.get(edge.target) || edge.target}`,
        source: idMap.get(edge.source) || edge.source,
        target: idMap.get(edge.target) || edge.target,
    }));

    return { nodes: transformedNodes, edges: transformedEdges };
};

/**
 * Generate next flow-position ID for a new node in a specific flow.
 */
export const getNextFlowPositionId = (nodes: { id: string }[], flowNumber: number): string => {
    const flowPrefix = `${flowNumber}-`;
    const existingPositions = nodes
        .filter((n) => n.id.startsWith(flowPrefix))
        .map((n) => parseInt(n.id.split("-")[1], 10))
        .filter((p) => !isNaN(p));

    const maxPosition = existingPositions.length > 0 ? Math.max(...existingPositions) : 0;
    return `${flowNumber}-${maxPosition + 1}`;
};

/**
 * Get the flow number for a node based on its ID.
 */
export const getFlowNumber = (nodeId: string): number => {
    const parts = nodeId.split("-");
    const parsed = parseInt(parts[0], 10);
    // Only fall back to 1 if NaN, preserve flow 0 for orphan nodes
    return Number.isNaN(parsed) ? 1 : parsed;
};
